<template>
  <div>
    <!-- Hero -->
    <section class="pt-11 pb-8 pt-md-12 pb-md-10 hero-section">
      <div class="container">
        <div class="row justify-content-center text-center">
          <div class="col-12 col-md-8">
            <p class="text-uppercase text-muted fw-bold mb-2" style="letter-spacing:.1em;font-size:.75rem">Developer Reviews</p>
            <h1 class="display-4 fw-bold mb-3">What developers say about {{ $state.siteName }}</h1>
            <p class="text-muted mb-5">Real feedback from developers using {{ $state.siteName }} for Instagram and TikTok data.</p>

            <!-- Trustpilot badge -->
            <a :href="trustpilotUrl" target="_blank" rel="noopener" class="tp-badge d-inline-flex align-items-center gap-3 text-decoration-none">
              <div class="d-flex align-items-center gap-2">
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <rect width="24" height="24" rx="3" fill="#00b67a"/>
                  <path d="M12 4l2.09 6.26H20l-5.14 3.63 1.85 6.11L12 16.27l-4.71 3.73 1.85-6.11L4 9.26h5.91L12 4z" fill="white"/>
                </svg>
                <span class="fw-bold tp-label" style="font-size:.95rem">Trustpilot</span>
              </div>
              <div class="tp-divider"></div>
              <div class="text-start">
                <div class="d-flex gap-1 mb-1">
                  <span v-for="n in 5" :key="n" class="tp-star">
                    <svg width="12" height="12" viewBox="0 0 12 12" fill="white"><path d="M6 1l1.5 3h3l-2.5 2 1 3L6 7.5 3 9l1-3L1.5 4h3z"/></svg>
                  </span>
                </div>
                <div class="tp-caption"><strong>3.8</strong> · Great · 2 reviews</div>
              </div>
            </a>

            <p v-if="lowestPrice" class="mt-3 mb-0 small">
              <span class="tp-caption">Pay-per-request pricing from <strong>${{ lowestPrice }}</strong> / request — </span>
              <router-link to="/pricing" class="tp-caption text-decoration-underline">see all plans →</router-link>
            </p>
          </div>
        </div>
      </div>
    </section>

    <!-- Reviews grid -->
    <section class="py-8 py-md-10">
      <div class="container">
        <p v-if="reviews.length" class="text-uppercase text-muted fw-bold mb-5" style="letter-spacing:.1em;font-size:.75rem">
          <strong class="text-light">{{ avgRating }} / 5</strong> · {{ reviews.length }} testimonials collected from
          <a :href="`mailto:${$state.siteEmail}`" class="text-muted">email</a> &amp;
          <a :href="`${$state.siteTgBase}/${$state.siteTgSupport}`" target="_blank" rel="noopener" class="text-muted">Telegram</a>
        </p>
        <div class="row">
          <div
            v-for="(review, index) in reviews"
            :key="index"
            class="col-12 col-md-6 col-lg-4 mb-4"
          >
            <div class="card review-card h-100 lift border-0">
              <div class="card-body d-flex flex-column p-5">
                <div class="quote-mark mb-2">&ldquo;</div>
                <p class="flex-grow-1 review-body mb-4">{{ review.body }}</p>
                <div class="d-flex align-items-center pt-4 review-footer">
                  <div class="avatar me-3 flex-shrink-0" :style="{ background: avatarColor(index) }">
                    {{ initials(review.username) }}
                  </div>
                  <div class="overflow-hidden flex-grow-1">
                    <div class="fw-bold review-username text-truncate" style="font-size:.9rem">{{ review.username }}</div>
                    <div class="text-truncate" style="font-size:.8rem">
                      <a
                        v-if="review.company_url"
                        :href="review.company_url"
                        target="_blank"
                        rel="noopener"
                        class="text-muted text-decoration-none"
                      >{{ review.company_name }}</a>
                      <span v-else class="text-muted">{{ review.company_name || '&mdash;' }}</span>
                    </div>
                  </div>
                  <div class="text-muted ms-2 flex-shrink-0" style="font-size:.75rem">
                    {{ formatDate(review.reviewed_at) }}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- CTA -->
    <section class="py-8 cta-section text-center">
      <div class="container">
        <p class="text-muted mb-4">Like what you see? Leave a review and help other developers find us.</p>
        <a :href="trustpilotUrl" target="_blank" rel="noopener" class="btn btn-success lift">
          Write a review on Trustpilot
        </a>
      </div>
    </section>
  </div>
</template>

<script>
export default {
  mounted() {
    this.getReviews()
    this.$services.PlanLowest().get().then(r => {
      const p = r.body && r.body.price
      if (p) this.lowestPrice = (+p).toFixed(4).replace(/0+$/, '').replace(/\.$/, '')
    }).catch(() => {})
  },
  computed: {
    trustpilotUrl() {
      return `https://www.trustpilot.com/review/${this.$state.siteDomain}`
    },
    // Average rating, rounded to 1 decimal — matches AggregateRating
    // JSON-LD emitted by lm.steve.api.capi.routers.pages so the visible
    // value and the structured-data value agree (Google policy).
    avgRating() {
      if (!this.reviews.length) return '5.0'
      const sum = this.reviews.reduce((s, r) => s + (r.rating || 5), 0)
      return (sum / this.reviews.length).toFixed(1)
    }
  },
  methods: {
    getReviews() {
      this.$services.Review().get()
        .then(res => {
          this.reviews.push(...res.body)
        })
    },
    avatarColor(index) {
      const colors = ['#4361ee', '#7209b7', '#f72585', '#3a86ff', '#06d6a0', '#fb8500']
      return colors[index % colors.length]
    },
    initials(username) {
      return username ? username.charAt(0).toUpperCase() : '?'
    },
    formatDate(dateStr) {
      return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })
    }
  },
  data() {
    return {
      reviews: [],
      lowestPrice: null,
    }
  }
}
</script>

<style lang="scss" scoped>
@import '../scss/theme/variables';

.hero-section {
  background: rgba($graphite-light, 0.3);
  border-bottom: 1px solid $graphite-light;
}

.cta-section {
  background: rgba($graphite-light, 0.3);
  border-top: 1px solid $graphite-light;
}

.tp-badge {
  border: 1.5px solid #00b67a;
  border-radius: 12px;
  padding: 14px 22px;
  transition: box-shadow .15s;
  background: rgba($graphite-dark, 0.5);
}
.tp-badge:hover {
  box-shadow: 0 4px 16px rgba(0, 182, 122, .25);
}
.tp-label {
  color: $gray-200;
}
.tp-divider {
  width: 1px;
  height: 36px;
  background: $graphite-light;
}
.tp-star {
  width: 22px;
  height: 22px;
  background: #00b67a;
  border-radius: 2px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
.tp-caption {
  font-size: .8rem;
  color: $gray-500;

  strong {
    color: $gray-200;
  }
}

.review-card {
  background: $graphite;
  transition: box-shadow .2s ease, transform .2s ease;
}
.review-card:hover {
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.4);
}
.review-body {
  color: $gray-300;
}
.review-footer {
  border-top: 1px solid $graphite-light;
}
.review-username {
  color: $gray-200;
}

.quote-mark {
  font-size: 3rem;
  line-height: 1;
  color: $graphite-light;
  font-family: Georgia, serif;
  margin-bottom: -8px;
}
.avatar {
  width: 38px;
  height: 38px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 15px;
  color: #fff;
}
</style>
